import HighchartsPatternFill from 'highcharts-pattern-fill';
import Highcharts from 'highcharts/highstock';
import _ from 'lodash';
import moment from 'moment';
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import { fetchHolterEvents, formatEventStrip } from '@/entities/event';
import {
  EMITTER_CONSTANTS,
  CHART_COLOR,
  KEY_CANCEL,
  KEY_RECOIL,
  OTHER_EVENT_LIST,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEventV2,
} from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  DAY_EPOCH,
  ZOOM_SCALE_DEFAULT,
  getZoomScale,
  getZoomScaleIndexToSet,
  calculateZoomRangeOnSelect,
  createChartData,
  createMinutelyHrChartData,
  filterHourlySummaryRanges,
  getMinMaxChart,
  isValidHr,
  validateEcgDataMap,
} from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import { JUMP_DATE_STATUS } from '@/shared/model';
import {
  selectIsLoadingHrChart,
  selectIsShowAfibEvent,
  selectIsShowArtifactEvent,
  selectIsShowOtherEvent,
  selectJumpDate,
  selectJumpDateStatus,
  selectSelectedColumnInfo,
  selectSelectedDateValue,
  selectSelectedEventIndex,
  useReportStore,
} from '@/entities/report';
import type { HrChartHandle, HrChartProps } from './index';

HighchartsPatternFill(Highcharts);

/**
 * Owns the daily heart-rate Highcharts chart of the strips-management tab, from data loading through column selection, event highlighting and zooming. Reads the selected date, column, event index, series visibility flags and ECG disclosure from the report store, fetches the day's holter events and builds the minutely series, keeps the clicked column and its event zones highlighted, honours jump-date requests, and exposes arrow-key column stepping on the forwarded ref.
 *
 * @param props - Chart props carrying studyId, profileId, ecgDataMap and timezoneOffset.
 * @param ref - Forwarded ref given a handleClickArrowButton handle for stepping to the neighbouring column.
 * @returns The chart view model with isLoadingHrChart, zoomScale, zoomIndex, the zoom in, out and reset handlers and setExtremesRef.
 */
const useHrChart = (
  { timezoneOffset, ecgDataMap, studyId, profileId }: HrChartProps,
  ref: ForwardedRef<HrChartHandle>,
) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Stores the events and points behind the selected heart-rate column in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New selected-column contents, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const setSelectedEventIndexAction = useReportStore((s) => s.actions.setSelectedEventIndex);
  /**
   * Sets which event within the selected heart-rate column is highlighted, in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New selected event index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedEventIndex = (v: Parameters<typeof setSelectedEventIndexAction>[1]) =>
    setSelectedEventIndexAction(keyRecoil, v);
  const isShowArtifactEvent = useReportStore(selectIsShowArtifactEvent(keyRecoil));
  const isShowAfibEvent = useReportStore(selectIsShowAfibEvent(keyRecoil));
  const isShowOtherEvent = useReportStore(selectIsShowOtherEvent(keyRecoil));
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Sets the heart-rate chart loading flag in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New loading state, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const ecgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the heart-rate chart should jump to in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New jump date.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Sets the empty-data flag for the heart-rate chart in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New empty-data state, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  /**
   * Reads the jump-date status of the heart-rate tab straight from the report store. Applies the tab-keyed selector to a fresh store snapshot so the value is current rather than captured at render time.
   *
   * @returns The current jump-date status.
   */
  const getJumpDateStatus = () => selectJumpDateStatus(keyRecoil)(useReportStore.getState());
  const setJumpDateStatusAction = useReportStore((s) => s.actions.setJumpDateStatus);
  /**
   * Sets the jump-date status of the heart-rate chart in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New jump-date status, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpDateStatus = (v: Parameters<typeof setJumpDateStatusAction>[1]) =>
    setJumpDateStatusAction(keyRecoil, v);
  const setChartHrsDataAction = useReportStore((s) => s.actions.setChartHrsData);
  /**
   * Stores the heart-rate chart series data for the tab in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New chart heart-rate data, or an updater receiving the current data.
   * @returns Nothing.
   */
  const setChartHrsData = (v: Parameters<typeof setChartHrsDataAction>[1]) =>
    setChartHrsDataAction(keyRecoil, v);

  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);

  const selectedEcgDisclosure = useMemo(() => {
    if (!_.isEmpty(ecgDisclosureChange)) return ecgDisclosureChange;
    return ecgDisclosure;
  }, [ecgDisclosure, ecgDisclosureChange]);

  const chartRef = useRef<any>(null);
  const currentSelectedIndexRef = useRef<any>(null);
  const currentFoundEventRef = useRef<any>(null);
  const unsaveArtifactEventsZones = useRef<any[]>([]);
  const unsaveAfibEventsZones = useRef<any[]>([]);
  const unsaveOtherEventsZones = useRef<any[]>([]);
  const eventIndicatorsRef = useRef<any[]>([]);
  const isShowArtifactEventRef = useRef(isShowArtifactEvent);
  const isShowAfibEventRef = useRef(isShowAfibEvent);
  const isShowOtherEventRef = useRef(isShowOtherEvent);
  const isZoomBySelect = useRef(false);
  const setExtremesRef = useRef<any[]>([]);
  const originalUnsaveArtifactEventsZonesRef = useRef<any[]>([]);
  const originalUnsaveAfibEventsZonesRef = useRef<any[]>([]);
  const originalUnsaveOtherEventsZonesRef = useRef<any[]>([]);

  const [zoomScale, setZoomScale] = useState<any>();
  const [zoomIndex, setZoomIndex] = useState(0);
  const [hasLoadChart, setHasLoadChart] = useState(false);
  const [state, setState] = useMergeState<{ maxHr: any }>({
    maxHr: undefined,
  });

  /**
   * Reads the heart-rate point of one chart series at a known position. Indexes straight into the series data and yields nothing when the point has no y value.
   *
   * @param seriesIndex - Index of the chart series to read.
   * @param index - Position of the point within that series.
   * @returns The Highcharts point at that position, or undefined when it has no heart-rate value.
   */
  const findEleHrFromIndex = (seriesIndex: any, index: any) => {
    const selectedHr = chartRef.current.series[seriesIndex].data[index];
    if (_.isNil(selectedHr?.y)) {
      return undefined;
    }
    return selectedHr;
  };

  /**
   * Locates the heart-rate point of one chart series by its x-axis category. Looks the category up in the series xData to get its position, reads the point at that position, and yields nothing when the point has no y value.
   *
   * @param seriesIndex - Index of the chart series to search.
   * @param category - X-axis category, the point's epoch value.
   * @returns The matching Highcharts point, or undefined when that slot has no heart-rate value.
   */
  const findEleHrFromCategory = (seriesIndex: any, category: any) => {
    const { data, xData } = chartRef.current.series[seriesIndex];
    const index = xData?.indexOf(category);
    const selectedHr = data[index];
    if (_.isNil(selectedHr?.y)) {
      return undefined;
    }
    return selectedHr;
  };

  /**
   * Returns the heart-rate chart to its full-day view. Clears both x-axis extremes, drops the select-drag zoom marker and resets the zoom step index and scale.
   *
   * @returns Nothing.
   */
  const handleResetZoom = () => {
    chartRef.current.xAxis[0].setExtremes(null, null);
    isZoomBySelect.current = false;
    setZoomIndex(0);
    setZoomScale(undefined);
  };

  const handleSetMinMaxNextExtremes = ({
    zoomScaleIndexToSet,
    min,
    max,
    dataMin,
    dataMax,
  }: any) => {
    const nextZoomRange = DAY_EPOCH / (ZOOM_SCALE_DEFAULT[zoomScaleIndexToSet] / 100);
    if (!isZoomBySelect.current && currentSelectedIndexRef.current) {
      const selectedHr = findEleHrFromIndex(8, currentSelectedIndexRef.current);
      const tempMin = selectedHr.category - nextZoomRange / 2;
      const tempMax = selectedHr.category + nextZoomRange / 2;
      const outOfRangeMax = tempMax - dataMax;
      const outOfRangeMin = tempMin - dataMin;
      const isOutOfRangeMax = outOfRangeMax > 0;
      const isOutOfRangeMin = outOfRangeMin < 0;
      let calculateMin;
      let calculateMax;
      if (isOutOfRangeMax) {
        calculateMin = tempMin - Math.abs(outOfRangeMax);
        calculateMax = dataMax;
      } else if (isOutOfRangeMin) {
        calculateMin = dataMin;
        calculateMax = tempMax + Math.abs(outOfRangeMin);
      } else {
        calculateMin = tempMin;
        calculateMax = tempMax;
      }
      chartRef.current.xAxis[0].setExtremes(calculateMin, calculateMax);
    } else {
      chartRef.current.xAxis[0].setExtremes(min, min + nextZoomRange);
    }
  };

  /**
   * Zooms the heart-rate chart out one step. Derives the current scale from the axis extremes and picks the next looser step, falling back to a full reset when that step is the whole day, otherwise applies the extremes for the step and stores the new index while clearing any drag-selected scale.
   *
   * @returns Nothing.
   */
  const handleZoomOut = () => {
    const { min, max, dataMax, dataMin } = chartRef.current.xAxis[0];
    const zoomScalee = getZoomScale(min, max);
    const zoomScaleIndexToSet = getZoomScaleIndexToSet(zoomScalee);
    if (zoomScaleIndexToSet === 0) {
      handleResetZoom();
      return;
    }
    handleSetMinMaxNextExtremes({
      zoomScaleIndexToSet,
      min,
      max,
      dataMin,
      dataMax,
    });
    setZoomIndex(zoomScaleIndexToSet);
    setZoomScale(undefined);
  };

  /**
   * Zooms the heart-rate chart in one step. Derives the current zoom scale from the axis extremes, picks the next tighter step, applies the extremes for that step and stores the new step index while clearing any drag-selected scale.
   *
   * @returns Nothing.
   */
  const handleZoomIn = () => {
    const { min, max, dataMin, dataMax } = chartRef.current.xAxis[0];
    const zoomScalee = getZoomScale(min, max);
    const zoomScaleIndexToSet = getZoomScaleIndexToSet(zoomScalee, true);
    handleSetMinMaxNextExtremes({
      zoomScaleIndexToSet,
      min,
      max,
      dataMin,
      dataMax,
    });
    setZoomIndex(zoomScaleIndexToSet);
    setZoomScale(undefined);
  };

  /**
   * Keeps a newly selected heart-rate point inside the visible window while the chart is zoomed in. Does nothing below a zoom scale of 100 or when the point already sits between the current extremes, otherwise recentres the same-width window on the point and clamps it against the data minimum and maximum before applying the new extremes.
   *
   * @param selectedHr - Selected Highcharts point whose category is centred on.
   * @returns Nothing.
   */
  const handleSetRangeWhenZooming = (selectedHr: any) => {
    const { min, max, dataMax, dataMin } = chartRef.current.xAxis[0];
    const { category } = selectedHr;
    const range = calculateZoomRangeOnSelect({ min, max, dataMin, dataMax, category });
    if (range) {
      chartRef.current.xAxis[0].setExtremes(range.calculateMin, range.calculateMax);
    }
  };

  /**
   * Clears the highlight left on the previously selected heart-rate column. When a previous index is held, blanks the class name on that point in the three event series and hides the marker point, deferring the redraw to the caller.
   *
   * @returns Nothing.
   */
  const resetPreviousPoint = () => {
    if (!_.isNil(currentSelectedIndexRef.current)) {
      const { series } = chartRef.current;
      series[4].data[currentSelectedIndexRef.current].update(
        {
          className: '',
        },
        false,
      );
      series[5].data[currentSelectedIndexRef.current].update(
        {
          className: '',
        },
        false,
      );
      series[6].data[currentSelectedIndexRef.current].update(
        {
          className: '',
        },
        false,
      );
      series[8].data[currentSelectedIndexRef.current].update(
        {
          className: '--hidden',
          color: CHART_COLOR.TURQUOISE_BLUE,
        },
        false,
      );
    }
  };

  // *: Reset to original zones in cases:
  // - Click to single event
  // - Click to non-event column
  /**
   * Restores the artifact, AFib and other-event series to the zone colouring they had before an event was highlighted. Picks the series matching the previous event's type, reinstates every series from the stored original unsaved-zone refs and defers the redraw to the caller.
   *
   * @param previousEvent - Destructured from the argument object; the event that was highlighted, whose type selects the series to restore.
   * @returns Nothing.
   */
  const resetToOriginalZones = ({ previousEvent }: any) => {
    const { series } = chartRef.current;
    const previousEventType = previousEvent?.type;
    const isArtifactEvent = previousEventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
    const isAfibEvent = previousEventType === TYPE_RHYTHM_EVENT_ENUM.AFIB;
    const newZones = isArtifactEvent
      ? originalUnsaveArtifactEventsZonesRef.current
      : isAfibEvent
        ? originalUnsaveAfibEventsZonesRef.current
        : originalUnsaveOtherEventsZonesRef.current;
    const seriesIndex = isArtifactEvent ? 1 : isAfibEvent ? 2 : 3;
    const updateSeries = {
      1: originalUnsaveArtifactEventsZonesRef.current,
      2: originalUnsaveAfibEventsZonesRef.current,
      3: originalUnsaveOtherEventsZonesRef.current,
    };
    updateSeries[seriesIndex] = newZones;
    Object.entries(updateSeries).forEach(([key, value]) => {
      series[key].update({ zones: value }, false);
    });
  };

  /**
   * Darkens the heart-rate chart zone covering the clicked rhythm event so its span stands out. Skips to a plain zone reset when the event starts and stops within the same minute, otherwise rounds the event bounds to minutes, splits or extends the artifact, AFib or other-event zone list with light and dark colours at those boundaries, sorts the zones by time and reapplies all three series without redrawing.
   *
   * @param foundEvent - Destructured from the argument object; the clicked event whose start and stop bound the highlight.
   * @param isArtifactEvent - Destructured from the argument object; true when the event is an artifact, selecting the grey series and palette.
   * @param isAfibEvent - Destructured from the argument object; true when the event is AFib, selecting the amber series and palette.
   * @returns Nothing.
   */
  const highlightSelectedEventArea = ({ foundEvent, isArtifactEvent, isAfibEvent }: any) => {
    const foundEventStop = moment(foundEvent.stop);
    const foundEventStart = moment(foundEvent.start);
    if (
      foundEventStart.hour() !== foundEventStop.hour() ||
      foundEventStart.minute() !== foundEventStop.minute()
    ) {
      const { series } = chartRef.current;
      const foundEventStartEpoch = foundEventStart.clone().startOf('minutes').valueOf();
      const foundEventStopEpoch = foundEventStop.clone().startOf('minutes').valueOf();
      let newZones = [
        ...(isArtifactEvent
          ? originalUnsaveArtifactEventsZonesRef.current
          : isAfibEvent
            ? originalUnsaveAfibEventsZonesRef.current
            : originalUnsaveOtherEventsZonesRef.current),
      ];
      let selectedLeftZone: any;
      let selectedRightZone: any;
      let selectedLeftZoneIndex: any;
      let selectedRightZoneIndex: any;
      _.forEach(newZones, (x, index) => {
        if (!_.isNil(x?.value) && foundEventStartEpoch >= x.value) {
          selectedLeftZone = x;
          selectedLeftZoneIndex = index;
        }
        if (!selectedRightZone && !_.isNil(x?.value) && foundEventStopEpoch <= x.value) {
          selectedRightZone = x;
          selectedRightZoneIndex = index;
        }
      });
      const color = isArtifactEvent
        ? CHART_COLOR.STEEL_GRAY_LIGHT
        : isAfibEvent
          ? CHART_COLOR.AMBER_YELLOW_LIGHT
          : CHART_COLOR.CRIMSON_RED_LIGHT;
      const selectedColor = isArtifactEvent
        ? CHART_COLOR.STEEL_GRAY_DARK
        : isAfibEvent
          ? CHART_COLOR.AMBER_YELLOW_DARK
          : CHART_COLOR.CRIMSON_RED_DARK;
      if (
        selectedLeftZoneIndex > -1 &&
        selectedLeftZone?.value === foundEventStartEpoch &&
        selectedRightZoneIndex > -1 &&
        selectedRightZone?.value === foundEventStopEpoch
      ) {
        // *: Case: click to event which is same to start and stop of exist event zone
        // (event is a event zone)
        newZones.splice(selectedRightZoneIndex, 1, {
          value: selectedRightZone.value,
          color: selectedColor,
          time: convertToMomentJs(selectedRightZone.value, timezoneOffset).format('HH:mm'),
        });
      } else if (selectedLeftZoneIndex > -1 && selectedLeftZone?.value === foundEventStartEpoch) {
        // *: Case: click to event which is same to start and different from stop of exist event zone
        // (event is a part of event zone)
        newZones.push({
          value: foundEventStopEpoch,
          color: selectedColor,
          time: convertToMomentJs(foundEventStopEpoch, timezoneOffset).format('HH:mm'),
        });
      } else if (selectedRightZoneIndex > -1 && selectedRightZone?.value === foundEventStopEpoch) {
        // *: Case: click to event which is different from start and same to stop of exist event zone
        // (event is a part of event zone)
        newZones.push({
          value: foundEventStartEpoch,
          color,
          time: convertToMomentJs(foundEventStartEpoch, timezoneOffset).format('HH:mm'),
        });
        newZones.splice(selectedRightZoneIndex, 1, {
          value: selectedRightZone.value,
          color: selectedColor,
          time: convertToMomentJs(selectedRightZone.value, timezoneOffset).format('HH:mm'),
        });
      } else {
        // *: Case: click to event which is different from start and stop of exist event zone
        // (event is a part of event zone)
        newZones.push(
          {
            value: foundEventStartEpoch,
            color,
            time: convertToMomentJs(foundEventStartEpoch, timezoneOffset).format('HH:mm'),
          },
          {
            value: foundEventStopEpoch,
            color: selectedColor,
            time: convertToMomentJs(foundEventStopEpoch, timezoneOffset).format('HH:mm'),
          },
        );
      }
      newZones = _.orderBy(newZones, ['value'], ['asc']);
      const seriesIndex = isArtifactEvent ? 1 : isAfibEvent ? 2 : 3;
      // reset all 3 series
      const updateSeries = {
        1: originalUnsaveArtifactEventsZonesRef.current,
        2: originalUnsaveAfibEventsZonesRef.current,
        3: originalUnsaveOtherEventsZonesRef.current,
      };
      updateSeries[seriesIndex] = newZones;
      Object.entries(updateSeries).forEach(([key, value]) => {
        series[key].update({ zones: value }, false);
      });
    } else {
      resetToOriginalZones({ previousEvent: currentFoundEventRef.current });
    }
  };

  const handleSelectColumn = ({
    series = chartRef.current.series,
    selectedHr,
    jumpOffset = 0,
    jumpStatus = JUMP_DATE_STATUS.NORMAL,
    chart = chartRef.current,
  }: any) => {
    if (!selectedHr) {
      return;
    }
    handleSetRangeWhenZooming(selectedHr);
    let foundEvent: any;
    const foundEvents: any[] = [];
    const selectedColumn = series[8].data[selectedHr.index];
    _.forEach(eventIndicatorsRef.current, (x) => {
      if (
        (x.type === TYPE_RHYTHM_EVENT_ENUM.AFIB && !isShowAfibEventRef.current) ||
        (x.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT && !isShowArtifactEventRef.current) ||
        !isShowOtherEventRef.current
      ) {
        return;
      }
      if (x.startIndex <= selectedColumn.index && x.stopIndex >= selectedColumn.index) {
        foundEvents.push(x);
      }
    });
    const tmpFoundEvent = foundEvents[0];
    if (tmpFoundEvent) {
      // *: Only select displayed event
      const isArtifactEvent = tmpFoundEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = tmpFoundEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      if (
        (isArtifactEvent && isShowArtifactEventRef.current) ||
        (isAfibEvent && isShowAfibEventRef.current) ||
        (!isArtifactEvent && !isAfibEvent && isShowOtherEventRef.current)
      ) {
        foundEvent = tmpFoundEvent;
      }
    }
    if (foundEvent) {
      const isArtifactEvent = foundEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = foundEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      series[4].data[selectedColumn.index].update(
        {
          className: '--hidden',
        },
        false,
      );
      series[5].data[selectedColumn.index].update(
        {
          className: '--hidden',
        },
        false,
      );
      series[6].data[selectedColumn.index].update(
        {
          className: '--hidden',
        },
        false,
      );
      selectedColumn.update(
        {
          className: isArtifactEvent
            ? '--artifact-event-clicked'
            : isAfibEvent
              ? '--afib-event-clicked'
              : '--event-clicked',
          color: isArtifactEvent
            ? CHART_COLOR.STEEL_GRAY
            : isAfibEvent
              ? CHART_COLOR.AMBER_YELLOW
              : CHART_COLOR.CRIMSON_RED,
        },
        false,
      );

      // highlightSelectedEventArea({ foundEvent, isArtifactEvent, isAfibEvent });

      const mainEvent = {
        ...foundEvent,
        index: selectedColumn.index,
        startTimeEpoch: selectedColumn.category,
        jumpOffset,
        jumpStatus,
      };

      const minuteInfo = {
        index: selectedColumn.index,
        id: selectedColumn.category,
        startTimeEpoch: selectedColumn.category,
        maxHr: selectedHr.maxHr,
        minHr: selectedHr.minHr,
        avgHr: selectedHr.avgHr,
        jumpOffset,
        jumpStatus,
      };
      const columnsInfo = [
        mainEvent,
        ..._.map(foundEvents, (event) => {
          if (event?.idEvent !== mainEvent?.idEvent) {
            return {
              ...event,
              index: selectedColumn.index,
              startTimeEpoch: selectedColumn.category,
              jumpOffset,
              jumpStatus,
            };
          }
          return null;
        }).filter(Boolean),
        minuteInfo,
      ];
      setSelectedColumnInfo(columnsInfo);
    } else {
      selectedColumn.update({ className: '--chart-clicked' }, false);
      // resetToOriginalZones({ previousEvent: currentFoundEventRef.current });
      const minuteInfo = {
        index: selectedColumn.index,
        id: selectedColumn.category,
        startTimeEpoch: selectedColumn.category,
        maxHr: selectedHr.maxHr,
        minHr: selectedHr.minHr,
        avgHr: selectedHr.avgHr,
        jumpOffset,
        jumpStatus,
      };
      setSelectedColumnInfo([minuteInfo]);
    }
    setSelectedEventIndex(0);

    // resetPreviousPoint uses previous data to clear so must set ref after
    resetPreviousPoint();
    currentSelectedIndexRef.current = selectedColumn.index;
    currentFoundEventRef.current = foundEvent || null;

    // *: Redraw chart after update
    if (chart && chart.legend) {
      chart.redraw();
    }

    if (jumpDate) {
      setJumpDate(undefined);
      setJumpDateStatus(JUMP_DATE_STATUS.NORMAL);
    }
  };

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext) => {
      handleSelectColumn({
        selectedHr: findEleHrFromIndex(
          9,
          isNext ? currentSelectedIndexRef.current + 1 : currentSelectedIndexRef.current - 1,
        ),
      });
    },
  }));

  /**
   * Builds the shaded band marking the ECG disclosure window over the selected day of the heart-rate chart. Returns an empty list when no disclosure is selected, otherwise walks all 1440 minutes from the day's start in the study time zone and emits the max-HR height for minutes inside the disclosure start and stop, or null elsewhere.
   *
   * @param maxHr - Height of the shaded band, defaulting to the chart's current maximum heart rate.
   * @returns An array of epoch and height point pairs, one per minute of the selected day.
   */
  const createEcgDisclosureData = (maxHr: any = state.maxHr) => {
    if (_.isEmpty(selectedEcgDisclosure)) return [];
    const selectedDateMoment = convertToMomentJs(selectedDateValue, timezoneOffset);
    const firstEpoch = selectedDateMoment.startOf('days').valueOf();
    const roundStartEpoch = moment(selectedEcgDisclosure.start).startOf('minutes').valueOf();
    const roundStopEpoch = moment(selectedEcgDisclosure.stop).startOf('minutes').valueOf();
    const ecgDisclosureAreaPoints: any[] = [];
    // if (roundStartMoment >= firstEpoch && roundStopMoment <= lastEpoch) {
    _.forEach(_.range(1440), (x) => {
      const epoch = firstEpoch + x * 60000;
      if (epoch >= roundStartEpoch && epoch <= roundStopEpoch) {
        ecgDisclosureAreaPoints.push([epoch, maxHr]);
      } else {
        ecgDisclosureAreaPoints.push([epoch, null]);
      }
    });
    // }
    return ecgDisclosureAreaPoints;
  };

  const createHrChart = ({
    hrs,
    guides,
    missingPoints,
    artifactEventGuides,
    artifactEventAreaPoints,
    afibEventGuides,
    afibEventAreaPoints,
    otherEventGuides,
    otherEventAreaPoints,
    unsaveArtifactEvents,
    unsaveAfibEvents,
    unsaveOtherEvents,
    minHr,
    maxHr,
    ecgDisclosureAreaPoints,
  }: any) => {
    const hrsData: any[] = _.map(hrs, (d, index) => {
      const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
      const avgHr = d[1];
      return {
        index,
        x: d[0],
        y: avgHr,
        id: d[0],
        maxHr: d[2],
        minHr: d[3],
        avgHr,
        tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
        events: {
          click: (e: any) => {
            if (e.point.index !== currentSelectedIndexRef.current) {
              handleSelectColumn({
                selectedHr: findEleHrFromIndex(9, e.point.index),
              });
            }
          },
        },
      };
    });
    const guidesData: any[] = _.map(guides, (d) => ({
      x: d[0],
      y: d[1],
      className: '--hidden',
    }));
    const missingPointsData: any[] = _.map(missingPoints, (d) => {
      const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
      return {
        x: d[0],
        y: d[1],
        tooltipText: `${time} - No data`,
      };
    });
    const artifactEventGuidesData: any[] = _.map(artifactEventGuides, (d) => ({
      x: d[0],
      y: d[1],
    }));
    const artifactEventAreaPointsData: any[] = _.map(artifactEventAreaPoints, (d) => {
      const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
      const avgHr = d[2];
      return {
        x: d[0],
        y: d[1],
        avgHr,
        tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
      };
    });
    const afibEventGuidesData: any[] = _.map(afibEventGuides, (d) => ({
      x: d[0],
      y: d[1],
    }));
    const afibEventAreaPointsData: any[] = _.map(afibEventAreaPoints, (d) => {
      const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
      const avgHr = d[2];
      return {
        x: d[0],
        y: d[1],
        avgHr,
        tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
      };
    });
    const otherEventGuidesData: any[] = _.map(otherEventGuides, (d) => ({
      x: d[0],
      y: d[1],
    }));
    const otherEventAreaPointsData: any[] = _.map(otherEventAreaPoints, (d) => {
      const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
      const avgHr = d[2];
      return {
        x: d[0],
        y: d[1],
        avgHr,
        tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
      };
    });
    const ecgDisclosureAreaPointsData: any[] = _.map(ecgDisclosureAreaPoints, (d) => ({
      x: d[0],
      y: d[1],
    }));

    const missingPointsZone: any[] = [];
    _.forEach(missingPointsData, (item, index) => {
      if (!_.isNil(hrsData[index].y)) {
        if (_.isNil(hrsData[index - 1]?.y)) {
          // *: This point is position that end missing point zone, start has data zone
          missingPointsZone.push({
            value: missingPointsData[index].x,
            color: 'url(#missing-data-ecg-disclosure-custom-pattern)',
          });
        }
        if (_.isNil(hrsData[index + 1]?.y)) {
          // *: This point is position that end has data zone, start missing point zone
          missingPointsZone.push({
            value: missingPointsData[index].x,
            color: CHART_COLOR.WHITE,
          });
        }
      } else if (_.isNil(hrsData[index + 1])) {
        // *: This point is the last point of chart and end missing point zone
        missingPointsZone.push({
          value: missingPointsData[index].x,
          color: 'url(#missing-data-ecg-disclosure-custom-pattern)',
        });
      }
    });

    unsaveArtifactEventsZones.current = [];
    _.forEach(unsaveArtifactEvents, (item) => {
      if (item.stopIndex > item.startIndex) {
        if (
          !_.find(
            unsaveArtifactEventsZones.current,
            (x) => x.value === artifactEventAreaPointsData[item.startIndex].x,
          )
        ) {
          unsaveArtifactEventsZones.current.push({
            value: artifactEventAreaPointsData[item.startIndex].x,
            color: 'rgba(130, 140, 153, 0.5)',
          });
        }
        unsaveArtifactEventsZones.current.push({
          value: artifactEventAreaPointsData[item.stopIndex].x,
          color: 'url(#unsave-artifact-event-ecg-disclosure-custom-pattern)',
        });
      } else {
        const foundGuide = _.find(
          artifactEventGuidesData,
          (guide) => guide.index === item.startIndex,
        );
        if (foundGuide) {
          foundGuide.color = 'url(#unsave-artifact-event-ecg-disclosure-custom-pattern)';
        }
      }
    });
    if (unsaveArtifactEventsZones.current.length) {
      unsaveArtifactEventsZones.current.push({ color: 'rgba(130, 140, 153, 0.5)' });
    }
    originalUnsaveArtifactEventsZonesRef.current = _.cloneDeep(unsaveArtifactEventsZones.current);

    unsaveAfibEventsZones.current = [];
    _.forEach(unsaveAfibEvents, (item) => {
      if (item.stopIndex > item.startIndex) {
        if (
          !_.find(
            unsaveAfibEventsZones.current,
            (x) => x.value === afibEventAreaPointsData[item.startIndex].x,
          )
        ) {
          unsaveAfibEventsZones.current.push({
            value: afibEventAreaPointsData[item.startIndex].x,
            color: 'rgba(255, 171, 0, 0.4)',
          });
        }
        unsaveAfibEventsZones.current.push({
          value: afibEventAreaPointsData[item.stopIndex].x,
          color: 'url(#unsave-afib-event-ecg-disclosure-custom-pattern)',
        });
      } else {
        const foundGuide = _.find(afibEventGuidesData, (guide) => guide.index === item.startIndex);
        if (foundGuide) {
          foundGuide.color = 'url(#unsave-afib-event-ecg-disclosure-custom-pattern)';
        }
      }
    });
    if (unsaveAfibEventsZones.current.length) {
      unsaveAfibEventsZones.current.push({ color: 'rgba(255, 171, 0, 0.4)' });
    }
    originalUnsaveAfibEventsZonesRef.current = _.cloneDeep(unsaveAfibEventsZones.current);

    unsaveOtherEventsZones.current = [];
    _.forEach(unsaveOtherEvents, (item) => {
      if (item.stopIndex > item.startIndex) {
        if (
          !_.find(
            unsaveOtherEventsZones.current,
            (x) => x.value === otherEventAreaPointsData[item.startIndex].x,
          )
        ) {
          unsaveOtherEventsZones.current.push({
            value: otherEventAreaPointsData[item.startIndex].x,
            color: 'rgba(242, 61, 66, 0.5)',
          });
        }
        unsaveOtherEventsZones.current.push({
          value: otherEventAreaPointsData[item.stopIndex].x,
          color: 'url(#unsave-other-event-ecg-disclosure-custom-pattern)',
        });
      } else {
        const foundGuide = _.find(otherEventGuidesData, (guide) => guide.index === item.startIndex);
        if (foundGuide) {
          foundGuide.color = 'url(#unsave-other-event-ecg-disclosure-custom-pattern)';
        }
      }
    });
    if (unsaveOtherEventsZones.current.length) {
      unsaveOtherEventsZones.current.push({ color: 'rgba(242, 61, 66, 0.5)' });
    }
    originalUnsaveOtherEventsZonesRef.current = _.cloneDeep(unsaveOtherEventsZones.current);

    const negativeHrsZones: any[] = [];
    _.forEach(hrsData, (item, index) => {
      if (index === hrsData.length - 1) {
        if (item.avgHr === -1 && hrsData[index - 1] && hrsData[index - 1]?.avgHr === -1) {
          negativeHrsZones.push({
            value: hrsData[index]?.x,
            dashStyle: 'dash',
          });
        }
      } else if (item.avgHr === -1) {
        if (
          hrsData[index - 1] &&
          (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
          hrsData[index + 1] &&
          (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
        ) {
          negativeHrsZones.push(
            { value: hrsData[index - 1]?.x },
            {
              value: hrsData[index + 1]?.x,
              dashStyle: 'dash',
            },
          );
        } else if (
          hrsData[index - 1] &&
          (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
          hrsData[index + 1] &&
          hrsData[index + 1]?.avgHr === -1
        ) {
          negativeHrsZones.push({ value: hrsData[index - 1]?.x });
        } else if (
          hrsData[index - 1] &&
          hrsData[index - 1]?.avgHr === -1 &&
          hrsData[index + 1] &&
          (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
        ) {
          negativeHrsZones.push({
            value: hrsData[index + 1]?.x,
            dashStyle: 'dash',
          });
        }
      }
    });

    negativeHrsZones.forEach((zone, index) => {
      if (zone.dashStyle && index === 0) {
        const dashHrs = hrsData.filter((item) => item.x <= zone.value);
        dashHrs.forEach((dashHr) => {
          const foundHr = hrsData.find((item) => item.x === dashHr.x);
          if (foundHr) {
            if (!_.isNil(foundHr.y)) {
              foundHr.y = minHr;
            }
          }
        });
      } else if (
        zone.dashStyle &&
        negativeHrsZones[index - 1] &&
        !negativeHrsZones[index - 1].dashStyle
      ) {
        const dashHrs = hrsData.filter(
          (item) => item.x >= negativeHrsZones[index - 1].value && item.x <= zone.value,
        );
        dashHrs.forEach((dashHr) => {
          const foundHr = hrsData.find((item) => item.x === dashHr.x);
          if (foundHr) {
            if (!_.isNil(foundHr.y)) {
              foundHr.y = minHr;
            }
          }
        });
      }
    });

    // chartHrsDataState (Recoil, Task 7) is typed IHrChartData[], which requires events/time/tooltipText/x/y
    // this mapped object doesn't set - pre-existing gap.
    setChartHrsData(
      _.map(hrsData, (x) => ({
        id: x.id,
        index: x.index,
        avgHr: x.avgHr,
        maxHr: x.maxHr,
        minHr: x.minHr,
      })) as any,
    );

    chartRef.current = Highcharts.stockChart('ecg-disclosure-container', {
      defs: {
        patterns: [
          {
            id: 'missing-data-ecg-disclosure-custom-pattern',
            path: {
              d: 'M0.207151 6H0V4.79294L4.79294 0H6V0.207151L0.207151 6Z M6 4.79294V6H4.79294L6 4.79294Z M0.207151 0L0 0.207151V0H0.207151Z',
              stroke: '#C6CBD1',
              strokeWidth: 1,
            },
            width: 6,
            height: 6,
          },
          {
            id: 'unsave-artifact-event-ecg-disclosure-custom-pattern',
            path: {
              d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
              stroke: CHART_COLOR.STEEL_GRAY,
              strokeWidth: 1,
              fill: 'rgba(130, 140, 153, 0.4)',
            },
            width: 4,
            height: 4,
          },
          {
            id: 'unsave-afib-event-ecg-disclosure-custom-pattern',
            path: {
              d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
              stroke: CHART_COLOR.AMBER_YELLOW,
              strokeWidth: 1,
              fill: 'rgba(255, 171, 0, 0.3)',
            },
            width: 4,
            height: 4,
          },
          {
            id: 'unsave-other-event-ecg-disclosure-custom-pattern',
            path: {
              d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
              stroke: CHART_COLOR.CRIMSON_RED,
              strokeWidth: 1,
              fill: 'rgba(242, 61, 66, 0.4)',
            },
            width: 4,
            height: 4,
          },
        ],
      },
      chart: {
        alignTicks: false,
        zoomType: 'x',
        selectionMarkerFill: 'rgba(0, 163, 173, 0.15)',
        animation: false,
        spacing: [0, 0, 0, 1],
        style: {
          fontFamily: 'Lato, Arial, Helvetica, sans-serif',
        },
        ignoreHiddenSeries: false,
        marginTop: 30,
        marginBottom: 50,
        events: {
          load: (e: any) => {
            setExtremesRef.current = [];
            const nextZoomRange = DAY_EPOCH / (ZOOM_SCALE_DEFAULT[0] / 100); // default 400%
            const { min } = e.target.xAxis[0];
            e.target.xAxis[0].setExtremes(min, min + nextZoomRange);
            setHasLoadChart(true);
          },
          click: (e: any) => {
            //* Built in function that auto find nearest point have data using KNNeighbor algorithm
            const selectedHr = chartRef.current.series[9].searchPoint(e, true);
            if (selectedHr?.index !== currentSelectedIndexRef.current) {
              handleSelectColumn({
                selectedHr,
              });
            }
          },
        },
      },
      series: [
        {
          type: 'area',
          data: missingPointsData,
          fillOpacity: 0.5,
          zones: missingPointsZone,
          tooltip: {
            headerFormat: '',
            pointFormat: '{point.tooltipText}',
          },
          zIndex: 10,
        },
        {
          type: 'area',
          data: artifactEventAreaPointsData,
          color: CHART_COLOR.STEEL_GRAY_LIGHT,
          zones: unsaveArtifactEventsZones.current,
          tooltip: {
            headerFormat: '',
            pointFormat: '{point.tooltipText}',
          },
          visible: isShowArtifactEvent,
        },
        {
          type: 'area',
          data: afibEventAreaPointsData,
          color: CHART_COLOR.AMBER_YELLOW_LIGHT,
          zones: unsaveAfibEventsZones.current,
          tooltip: {
            headerFormat: '',
            pointFormat: '{point.tooltipText}',
          },
          visible: isShowAfibEvent,
        },
        {
          type: 'area',
          data: otherEventAreaPointsData,
          color: CHART_COLOR.CRIMSON_RED_LIGHT,
          zones: unsaveOtherEventsZones.current,
          tooltip: {
            headerFormat: '',
            pointFormat: '{point.tooltipText}',
          },
          visible: isShowOtherEvent,
        },
        {
          type: 'column',
          data: artifactEventGuidesData,
          color: CHART_COLOR.STEEL_GRAY_LIGHT,
          tooltip: {
            pointFormatter: () => '',
            headerFormat: '',
            pointFormat: '',
          },
          enableMouseTracking: false,
          clip: false,
          visible: isShowArtifactEvent,
        },
        {
          type: 'column',
          data: afibEventGuidesData,
          color: CHART_COLOR.AMBER_YELLOW_LIGHT,
          tooltip: {
            pointFormatter: () => '',
            headerFormat: '',
            pointFormat: '',
          },
          enableMouseTracking: false,
          clip: false,
          visible: isShowAfibEvent,
        },
        {
          type: 'column',
          data: otherEventGuidesData,
          color: CHART_COLOR.CRIMSON_RED_LIGHT,
          tooltip: {
            pointFormatter: () => '',
            headerFormat: '',
            pointFormat: '',
          },
          enableMouseTracking: false,
          clip: false,
          visible: isShowOtherEvent,
        },
        {
          type: 'area',
          data: ecgDisclosureAreaPointsData,
          className: 'ecg-disclosure-area',
          tooltip: {
            pointFormatter: () => '',
            headerFormat: '',
            pointFormat: '',
          },
          enableMouseTracking: false,
          clip: false,
        },
        {
          type: 'column',
          data: guidesData,
          color: CHART_COLOR.TURQUOISE_BLUE,
          tooltip: {
            pointFormatter: () => '',
            headerFormat: '',
            pointFormat: '',
          },
          enableMouseTracking: false,
          clip: false,
        },
        {
          type: 'spline',
          data: hrsData,
          color: CHART_COLOR.CERULEAN_BLUE,
          zones: negativeHrsZones,
          tooltip: {
            headerFormat: '',
            pointFormat: '{point.tooltipText}',
          },
        },
      ],
      boost: {
        useGPUTranslations: true,
      },
      xAxis: [
        {
          tickInterval: 3600 * 1000,
          tickLength: 5,
          gridLineWidth: 0, // *: Hide x-axis grid
          crosshair: {
            color: CHART_COLOR.TURQUOISE_BLUE,
          },
          labels: {
            align: 'left',
            format: '{value:%H:%M}',
            step: 2,
          },
          minRange: 4317000, // 1.2 hours
          // *: Display scrollbar while zooming
          scrollbar: {
            enabled: true,
            zIndex: 0,
            showFull: false,
            height: 10,
            trackBackgroundColor: '#F5F7FA',
            barBackgroundColor: '#DCE1E5',
            rifleColor: '#DCE1E5',
            barBorderRadius: 5,
            barBorderWidth: 0,
            buttonBorderWidth: 0,
            trackBorderWidth: 0,
            trackBorderRadius: 5,
            buttonBorderRadius: 5,
            buttonBackgroundColor: 'transparent',
          },
          events: {
            afterSetExtremes: (e: any) => {
              const { min, max, trigger } = e;
              if (trigger === 'zoom') {
                isZoomBySelect.current = true;
              }
              const zoomScalee = getZoomScale(min, max);
              //* isZooming
              if (zoomScalee > 100) {
                setExtremesRef.current = [min, max];
              } else {
                setExtremesRef.current = [];
              }
              const foundZoomScaleIndex = _.findIndex(ZOOM_SCALE_DEFAULT, (x) => x === zoomScalee);
              if (foundZoomScaleIndex !== -1) {
                setZoomScale(undefined);
                setZoomIndex(foundZoomScaleIndex);
              } else {
                setZoomScale(zoomScalee);
              }
            },
          },
        },
      ],
      yAxis: [
        {
          title: {
            text: null,
          },
          opposite: true,
          min: minHr,
          max: maxHr,
          lineWidth: 1,
          tickLength: 5,
          tickWidth: 1,
          showLastLabel: true,
          labels: {
            align: 'left',
            x: 12,
            y: 3,
          },
          startOnTick: false, // *: Setting to display min/max
          stopOnTick: false, // *: Setting to display min/max
          tickPositioner: () => {
            // *: To determine displayed ticks
            const positions = [minHr, Math.ceil((maxHr + minHr) / 2), maxHr];
            return positions;
          },
        },
      ],
      title: {
        text: null,
      },
      tooltip: {
        animation: false,
        hideDelay: 0,
        useHTML: true,
        shared: true,
        split: false,
        backgroundColor: null,
        borderWidth: 0,
        style: {
          padding: 0,
        },
        /**
         * Renders the shared tooltip for a heart-rate chart column. Takes the first three points bound by Highcharts on the tooltip context and wraps the first available tooltipText in the hr-chart-tooltip container markup.
         *
         * @returns The tooltip HTML string for the hovered column.
         */
        formatter() {
          const [firstPoint, secondPoint, thirdPoint] = this.points;
          return `<div class="hr-chart-tooltip">${firstPoint.point.tooltipText || secondPoint.point.tooltipText || thirdPoint.point.tooltipText}</div>`;
        },
        positioner: (labelWidth: any, labelHeight: any, point: any) => {
          let calculateX;
          if (point.plotX + labelWidth / 2 > 925) {
            // overflow right
            calculateX = 815;
          } else if (point.plotX - labelWidth / 2 < 0) {
            // overflow left
            calculateX = -8;
          } else {
            calculateX = point.plotX - labelWidth / 2; //* plotX is crosshair position
          }
          return { x: calculateX, y: 1 };
        },
        shadow: false,
      },
      plotOptions: {
        area: {
          animation: false,
          shadow: false,
          dataLabels: { style: { textShadow: false } },
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
        series: {
          turboThreshold: 1440,
          animation: false,
          shadow: false,
          dataLabels: { style: { textShadow: false } },
          states: {
            hover: {
              enabled: false,
            },
          },
          marker: {
            enabled: false,
          },
          zoneAxis: 'x',
          grouping: false,
          dataGrouping: {
            enabled: false,
          },
        },
        column: {
          pointRange: 1, //* important if null some column will disappear when zoom 100%
          pointWidth: 0.5,
          minPointLength: 62, // *: Make guides always have the same height of chart
          animation: false,
          shadow: false,
          dataLabels: { style: { textShadow: false } },
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
        spline: {
          animation: false,
          shadow: false,
          dataLabels: { style: { textShadow: false } },
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
      },
      time: {
        timezoneOffset: -1 * timezoneOffset,
      },
      credits: {
        enabled: false,
      },
      navigator: {
        enabled: false,
      },
      rangeSelector: {
        enabled: false,
      },
      scrollbar: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
    } as any);
    setIsLoadingHrChart(false);
  };

  /**
   * Rebuilds the heart-rate chart for the selected day from the study's hourly ECG summaries and that day's holter events. Filters the summaries to the day, expands them into minutely average, max and min heart-rate arrays, derives the axis bounds from the averages, indexes each event to its minute offset ordered AFib then other then artifact, assembles the series and zones, overlays the ECG disclosure band and renders, flagging empty data when anything is missing.
   *
   * @param holterEventsByDate - Holter events falling inside the selected day, used to place the event indicators and zones.
   * @returns A promise resolving once the chart is rendered or the empty-data flag is set and loading has cleared.
   */
  const handleCreateHrData = async (holterEventsByDate: any[] = []) => {
    try {
      let hasNoData;
      const selectedDateMoment = convertToMomentJs(selectedDateValue, timezoneOffset);
      const { data, channels, samplingFrequency } = ecgDataMap;

      if (data?.length && selectedDateMoment && !data.find((x: any) => !x.dataUrl)) {
        const startDayMoment = selectedDateMoment.clone().startOf('days');
        const stopDayMoment = selectedDateMoment.clone().endOf('days');
        // *: Filter HR data in date
        const currentDateData: any[] = filterHourlySummaryRanges({
          summaries: ecgDataMap,
          start: startDayMoment,
          stop: stopDayMoment,
          channels,
          samplingFrequency,
          willAddDataRanges: false,
        } as any);
        const isValidCurrentDateData = validateEcgDataMap(currentDateData);
        if (isValidCurrentDateData) {
          const filteredAvgHrArray: any[] = [];
          const filteredMaxHrArray: any[] = [];
          const filteredMinHrArray: any[] = [];
          _.forEach(currentDateData, (item) => {
            filteredAvgHrArray.push(item.avgHrs);
            filteredMaxHrArray.push(item.maxHrs);
            filteredMinHrArray.push(item.minHrs);
          });
          const [avgHrArray, maxHrArray, minHrArray]: any[] = createMinutelyHrChartData({
            hourlySummaries: currentDateData,
            minutelyHrs: filteredAvgHrArray,
            start: startDayMoment,
            stop: stopDayMoment,
            maxMinutelyHrs: filteredMaxHrArray,
            minMinutelyHrs: filteredMinHrArray,
            isEditReportPage: true,
          } as any);

          if (_.find(avgHrArray, (x) => !_.isNil(x))) {
            // *: All data are null
            let minHr = 0;
            let maxHr = 150;
            const clonedAvgHrArray = [...avgHrArray];
            _.remove(clonedAvgHrArray, (x) => x < 0);
            if (clonedAvgHrArray.length > 0) {
              const minHrTmp = Math.floor(_.min(clonedAvgHrArray) / 10) * 10;
              const maxHrTmp = Math.ceil(_.max(clonedAvgHrArray) / 10) * 10;
              const { min, max } = getMinMaxChart(minHrTmp, maxHrTmp);
              minHr = min || 0;
              maxHr = max || 150;
            }

            const firstEpoch = startDayMoment.valueOf();
            const eventIndicators = _.sortBy(
              _.map(holterEventsByDate, (holterEvent) => {
                const roundStartMoment = moment(holterEvent.start).startOf('minutes');
                const roundStopMoment = moment(holterEvent.stop).startOf('minutes');
                return {
                  ...holterEvent,
                  startIndex: (roundStartMoment.valueOf() - firstEpoch) / 1000 / 60,
                  stopIndex: (roundStopMoment.valueOf() - firstEpoch) / 1000 / 60,
                };
              }),
              [
                //* Event order: Afib -> other -> artifact -> minute info
                (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
                (event) => !OTHER_EVENT_LIST.includes(event.type),
                (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
              ],
            );

            const {
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
            } = createChartData({
              hrArray: avgHrArray,
              eventIndicators,
              firstEpoch,
              maxHr, //* use max hr to fill null data and draw zone
              maxHrArray,
              minHrArray,
              isEditReportPage: true,
            });

            const ecgDisclosureAreaPoints = createEcgDisclosureData(maxHr);
            setState({ maxHr });

            eventIndicatorsRef.current = eventIndicators;
            createHrChart({
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
              minHr,
              maxHr,
              ecgDisclosureAreaPoints,
            });
          } else {
            hasNoData = true;
          }
        } else {
          hasNoData = true;
        }
      } else {
        hasNoData = true;
      }
      if (hasNoData) {
        setIsEmptyData(true);
      }
    } catch (error) {
      logError('Failed to fetch HR data: ', error);
      toastrError(error.message, 'Error');
      setIsEmptyData(true);
    }
    setIsLoadingHrChart(false);
  };

  /**
   * Loads the holter events for the day shown on the heart-rate chart. Raises the chart loading flag, queries the persisted event types over the selected day's 24-hour window under the holter-AI cancel key, formats the returned events into strips, logs and swallows any failure, then rebuilds the chart heart-rate data with whatever was retrieved.
   *
   * @returns A promise resolving once the events are fetched and the chart data rebuild has been kicked off.
   */
  const handleFetchHolterEventsByDateV2 = async () => {
    setIsLoadingHrChart(true);
    let holterEventsByDate: any[] = [];
    try {
      const holterEventsFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate: selectedDateValue,
        stopSearchDate: moment(selectedDateValue).add(1, 'days').toISOString(),
      };
      const { events: downloadedHolterEvents } = await fetchHolterEvents(
        holterEventsFilter,
        0,
        true,
        KEY_CANCEL.API_HOLTER_AI,
      );
      holterEventsByDate = formatEventStrip(downloadedHolterEvents);
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
    handleCreateHrData(holterEventsByDate);
  };

  useEffect(() => {
    if (hasLoadChart) {
      const { series } = chartRef.current;
      let selectedHr;
      if (jumpDate) {
        const clickedCategory = moment(jumpDate).startOf('minutes').valueOf();
        selectedHr = findEleHrFromCategory(9, clickedCategory);
      } else if (currentSelectedIndexRef.current) {
        selectedHr = findEleHrFromIndex(9, currentSelectedIndexRef.current);
      } else {
        selectedHr = _.find(series[9].data, (item) => !_.isNil(item.y));
      }
      handleSelectColumn({
        selectedHr,
        jumpStatus: getJumpDateStatus(),
      });
      setHasLoadChart(false);
    }
  }, [hasLoadChart]);

  useEffect(() => {
    // *: Change to other date
    if (selectedDateValue) {
      currentSelectedIndexRef.current = null;
      currentFoundEventRef.current = null;
      handleFetchHolterEventsByDateV2();
    }
  }, [selectedDateValue]);

  // useEmitter(EMITTER_CONSTANTS.RE_RENDER_HR_CHART, handleFetchHolterEventsByDateV2);

  useEmitter(EMITTER_CONSTANTS.RE_RENDER_HR_CHART, handleFetchHolterEventsByDateV2, [
    selectedDateValue,
  ]);

  useUpdateEffect(() => {
    // *: Jump same date
    if (!_.isEmpty(chartRef.current) && jumpDate) {
      const clickedTime = moment(jumpDate);
      const clickedCategory = clickedTime.clone().startOf('minutes').valueOf();
      handleSelectColumn({
        selectedHr: findEleHrFromCategory(9, clickedCategory),
        jumpOffset: clickedTime.valueOf(),
        jumpStatus: getJumpDateStatus(),
      });
    }
  }, [jumpDate]);

  useUpdateEffect(() => {
    if (!_.isNil(currentSelectedIndexRef.current) && !_.isEmpty(chartRef.current)) {
      // selectedColumnInfoState (Recoil, Task 7) is typed (IEvent | IPoint)[]; type/oldType are
      // event-only fields that IPoint lacks - pre-existing gap.
      const selectedEvent: any = _.isEmpty(selectedColumnInfo)
        ? null
        : selectedColumnInfo[selectedEventIndex];
      if (selectedEvent?.type) {
        const eventType = selectedEvent.type || selectedEvent.oldType;
        const isArtifactEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
        const isAfibEvent = eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB;
        chartRef.current.series[8].data[currentSelectedIndexRef.current].update(
          {
            className: isArtifactEvent
              ? '--artifact-event-clicked'
              : isAfibEvent
                ? '--afib-event-clicked'
                : '--event-clicked',
            color: isArtifactEvent
              ? CHART_COLOR.STEEL_GRAY
              : isAfibEvent
                ? CHART_COLOR.AMBER_YELLOW
                : CHART_COLOR.CRIMSON_RED,
          },
          false,
        );
        highlightSelectedEventArea({ foundEvent: selectedEvent, isArtifactEvent, isAfibEvent });
      } else {
        chartRef.current.series[8].data[currentSelectedIndexRef.current].update(
          {
            className: '--chart-clicked',
            color: CHART_COLOR.TURQUOISE_BLUE,
          },
          false,
        );
        resetToOriginalZones({ previousEvent: currentFoundEventRef.current });
      }
      if (chartRef.current) {
        chartRef.current.redraw();
      }
    }
  }, [selectedEventIndex, selectedColumnInfo]);

  useUpdateEffect(() => {
    isShowArtifactEventRef.current = isShowArtifactEvent;
    isShowAfibEventRef.current = isShowAfibEvent;
    isShowOtherEventRef.current = isShowOtherEvent;
    if (chartRef.current) {
      chartRef.current.series[1].setVisible(isShowArtifactEvent, false);
      chartRef.current.series[4].setVisible(isShowArtifactEvent, false);
      chartRef.current.series[2].setVisible(isShowAfibEvent, false);
      chartRef.current.series[5].setVisible(isShowAfibEvent, false);
      chartRef.current.series[3].setVisible(isShowOtherEvent, false);
      chartRef.current.series[6].setVisible(isShowOtherEvent, false);
      handleSelectColumn({
        selectedHr: findEleHrFromIndex(9, currentSelectedIndexRef.current),
      });
    }
  }, [isShowArtifactEvent, isShowAfibEvent, isShowOtherEvent]);

  useEffect(() => {
    if (!_.isEmpty(selectedEcgDisclosure) && chartRef.current) {
      const ecgDisclosureAreaPoints = createEcgDisclosureData();
      const ecgDisclosureAreaPointsData: any[] = _.map(ecgDisclosureAreaPoints, (d) => ({
        x: d[0],
        y: d[1],
      }));
      chartRef.current.series[7].update({
        data: ecgDisclosureAreaPointsData,
      });
      setJumpDate(selectedEcgDisclosure.start);
    }
  }, [selectedEcgDisclosure]);

  return {
    isLoadingHrChart,
    zoomScale,
    zoomIndex,
    handleZoomOut,
    handleZoomIn,
    handleResetZoom,
    setExtremesRef,
  };
};

export default useHrChart;
